import React, { useState, useEffect } from 'react';
import { useModel, useNavigate } from '@umijs/max';
import { Dropdown, MenuProps, Tabs } from 'antd';
import { useAliveController } from 'react-activation';
import {
  MoreOutlined,
  ReloadOutlined,
  CloseOutlined,
  StopOutlined,
  PicCenterOutlined,
  VerticalRightOutlined,
  VerticalLeftOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import useStyles from './style';
import { TabItem } from '@/models/system';
import { useI18n } from '@/hooks/useI18n';

type TargetKey = React.MouseEvent | React.KeyboardEvent | string;

const MultiTab: React.FC = () => {
  const { styles } = useStyles();
  const { t } = useI18n();
  const {
    tabList,
    activeKey,
    removeTab,
    removeOtherTabs,
    removeLeftTabs,
    removeRightTabs,
    removeAllTabs,
    closeAndSwitchTab,
  } = useModel('system');
  const { dropScope, refreshScope } = useAliveController();
  const navigate = useNavigate();

  // 待清理的 KeepAlive Name
  const [pendingDropName, setPendingDropName] = useState<string | null>(null);

  // 延迟清理 Effect：防止关闭当前 Tab 时闪烁
  useEffect(() => {
    if (pendingDropName) {
      // 等待路由切换完成后再清理缓存
      const timer = setTimeout(() => {
        dropScope(pendingDropName);
        setPendingDropName(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [pendingDropName, dropScope]);

  const handleClose = (targetKey: TargetKey) => {
    const key = targetKey as string;
    const targetTab = tabList.find((t) => t.key === key);
    if (!targetTab) return;

    const { keepAliveName } = targetTab;
    const isActive = key === activeKey;

    closeAndSwitchTab(key, () => {
      if (keepAliveName) {
        if (isActive) {
          setPendingDropName(keepAliveName);
        } else {
          dropScope(keepAliveName);
        }
      }
    });
  };

  const handleTabClick = (key: string) => {
    const tab = tabList.find((t) => t.key === key);
    if (tab) {
      navigate(tab.pathname);
    }
  };

  const handleRefresh = (key: string) => {
    const tab = tabList.find((t) => t.key === key);
    if (tab && tab.keepAliveName) {
      refreshScope(tab.keepAliveName);
    }
  };

  const renderContextMenu = (tab: TabItem) => {
    const menuItems: MenuProps['items'] = [
      {
        key: 'refresh',
        label: t('component.multiTab.refresh', '重新加载'),
        icon: <ReloadOutlined />,
        onClick: () => handleRefresh(tab.key),
      },
      {
        key: 'close',
        label: t('component.multiTab.close', '关闭标签页'),
        icon: <CloseOutlined />,
        disabled: tab.closable === false,
        onClick: () => handleClose(tab.key),
      },
      {
        key: 'closeLeft',
        label: t('component.multiTab.closeLeft', '关闭左侧标签页'),
        icon: <VerticalRightOutlined />,
        onClick: () => {
          const currentIndex = tabList.findIndex((t) => t.key === tab.key);
          const tabsToRemove = tabList.filter(
            (t, index) => index < currentIndex && t.closable !== false,
          );
          tabsToRemove.forEach((t) => {
            if (t.keepAliveName) dropScope(t.keepAliveName);
          });
          removeLeftTabs(tab.key);
        },
      },
      {
        key: 'closeRight',
        label: t('component.multiTab.closeRight', '关闭右侧标签页'),
        icon: <VerticalLeftOutlined />,
        onClick: () => {
          const currentIndex = tabList.findIndex((t) => t.key === tab.key);
          const tabsToRemove = tabList.filter(
            (t, index) => index > currentIndex && t.closable !== false,
          );
          tabsToRemove.forEach((t) => {
            if (t.keepAliveName) dropScope(t.keepAliveName);
          });
          removeRightTabs(tab.key);
        },
      },
      {
        key: 'closeOthers',
        label: t('component.multiTab.closeOthers', '关闭其它标签页'),
        icon: <PicCenterOutlined />,
        onClick: () => {
          const tabsToRemove = tabList.filter(
            (t) => t.key !== tab.key && t.closable !== false,
          );
          tabsToRemove.forEach((t) => {
            if (t.keepAliveName) dropScope(t.keepAliveName);
          });
          removeOtherTabs(tab.key);
          if (activeKey !== tab.key) navigate(tab.pathname);
        },
      },
      {
        key: 'closeAll',
        label: t('component.multiTab.closeAll', '关闭全部标签页'),
        icon: <StopOutlined />,
        onClick: () => {
          const tabsToRemove = tabList.filter((t) => t.closable !== false);
          tabsToRemove.forEach((t) => {
            if (t.keepAliveName) dropScope(t.keepAliveName);
          });
          removeAllTabs();
          // 导航到第一个不可关闭的标签（通常是首页）
          const firstUnclosable = tabList.find((t) => t.closable === false);
          if (firstUnclosable) navigate(firstUnclosable.pathname);
          else navigate('/');
        },
      },
    ];
    return { items: menuItems };
  };

  const moreMenuItems: MenuProps['items'] = [
    ...tabList.map((tab) => ({
      key: tab.key,
      label: (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            minWidth: 100,
          }}
        >
          <span style={{ marginRight: 12 }}>{tab.title}</span>
          {tab.closable !== false && (
            <CloseOutlined
              onClick={(e) => {
                e.stopPropagation();
                handleClose(tab.key);
              }}
              style={{ fontSize: 10 }}
            />
          )}
        </div>
      ),
      onClick: () => handleTabClick(tab.key),
    })),
    {
      type: 'divider',
    },
    {
      key: 'closeOthers',
      label: t('component.multiTab.closeOthers', '关闭其它标签页'),
      icon: <PicCenterOutlined />,
      onClick: () => {
        const currentTab = tabList.find((t) => t.key === activeKey);
        if (currentTab) {
          const tabsToRemove = tabList.filter(
            (t) => t.key !== activeKey && t.closable !== false,
          );
          tabsToRemove.forEach((t) => {
            if (t.keepAliveName) dropScope(t.keepAliveName);
          });
          removeOtherTabs(activeKey);
        }
      },
    },
    {
      key: 'closeAll',
      label: t('component.multiTab.closeAll', '关闭全部标签页'),
      icon: <StopOutlined />,
      onClick: () => {
        const tabsToRemove = tabList.filter((t) => t.closable !== false);
        tabsToRemove.forEach((t) => {
          if (t.keepAliveName) dropScope(t.keepAliveName);
        });
        removeAllTabs();
        const firstUnclosable = tabList.find((t) => t.closable === false);
        if (firstUnclosable) navigate(firstUnclosable.pathname);
        else navigate('/');
      },
    },
  ];

  return (
    <div className={styles.multiTabWrapper}>
      <Tabs
        hideAdd
        type="editable-card"
        activeKey={activeKey}
        onChange={handleTabClick}
        onEdit={(targetKey, action) => {
          if (action === 'remove') {
            handleClose(targetKey);
          }
        }}
        tabBarExtraContent={
          <Dropdown
            menu={{
              items: moreMenuItems,
              style: { maxHeight: 400, overflowY: 'auto' },
            }}
            placement="bottomRight"
          >
            <div className={styles.tabOperationBtn}>
              <SettingOutlined />
            </div>
          </Dropdown>
        }
        items={tabList.map((tab) => ({
          label: (
            <Dropdown
              menu={renderContextMenu(tab)}
              trigger={['contextMenu']}
            >
              <div
                style={{
                  display: 'inline-block',
                  width: '100%',
                  height: '100%',
                }}
              >
                {tab.title}
              </div>
            </Dropdown>
          ),
          key: tab.key,
          closable: tab.closable,
        }))}
      />
    </div>
  );
};

export default MultiTab;
